<template>
	<EventLayout :event="event" :subtitle="activity.title" :return-url="route('events.packs.edit', {event: event.id, pack: pack.id})" :return-label="pack.name">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="save">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<div class="grid grid-cols-2 w-full gap-3">
			<div class="col-span-2 flex items-end gap-3">
				<Switch v-model.number="activity.active" :label="trans('events.activities.active')"/>
				<Switch v-model.number="activity.mandatory" :label="trans('events.activities.mandatory')"/>
			</div>
			<LabeledObject required>
				<template #label>{{ trans('events.activities.begins_at') }}</template>
				<IconDateTimeInput v-model="activity.begins_at" icon-class="fa-solid fa-hourglass-start" full/>
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('events.activities.ends_at') }}</template>
				<IconDateTimeInput v-model="activity.ends_at" icon-class="fa-solid fa-hourglass-end" full/>
			</LabeledObject>
			<LabeledObject required class="col-span-2">
				<template #label>{{ trans('general.title') }}</template>
				<input v-model="activity.title" type="text">
			</LabeledObject>
			<LabeledObject class="col-span-2">
				<template #label>{{ trans('general.subtitle') }}</template>
				<input v-model="activity.subtitle" type="text">
			</LabeledObject>
			<LabeledObject class="col-span-2" :description="trans('events.activities.disclaimer_description')">
				<template #label>{{ trans('events.activities.disclaimer') }}</template>
				<textarea v-model="activity.disclaimer"/>
			</LabeledObject>
			<Card :title="trans('events.activities.maximum')" class="col-span-2">
				<div class="flex items-end gap-3">
					<IconNumberInput v-model.number="activity.maximum" icon-class="fa-solid fa-ticket"/>
					<Switch v-model.number="activity.counts_toward_participation" :label="trans('events.activities.counts_toward_participation')"/>
				</div>
			</Card>
			<LabeledObject v-if="activity.type === ActivityScope.Course" class="col-span-2">
				<template #label>{{ trans('events.activities.course.passing_mark') }}</template>
				<IconNumberInput v-model="activity.passing_mark" :min="0" :max="100" :step="1" icon-class="fa-solid fa-graduation-cap"/>
			</LabeledObject>
			<Card v-if="activity.type === ActivityScope.FieldTrip" :title="trans('events.activities.fieldTrip.company_id')" class="col-span-2">
				<div v-if="activity.company" class="border rounded px-3 py-2 flex items-center justify-between gap-3 w-full dark:bg-gray-900 dark:border-gray-700">
					<span>{{ activity.company.name }}</span>
					<CloseSelected @click.stop="unselectCompany"/>
				</div>
				<SearchModel v-else attribute="name" :label="trans('general.search')" search-type="companies" @selected="selectCompany(activity.company)"/>
			</Card>
			<LabeledObject class="col-span-2">
				<template #label>{{ trans('events.activities.description') }}</template>
				<TextEditor v-model="activity.description" bordered/>
			</LabeledObject>
		</div>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import EventLayout from '@/Layouts/EventLayout.vue'
import {type Company} from '@/Types/Settings/Company'
import Switch from '@/Components/UI/Switches/Switch.vue'
import {ActivityScope} from '@/Types/Enums/ActivityScope'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SearchModel from '@/Components/Search/SearchModel.vue'
import TextEditor from '@/Components/UI/Editor/TextEditor.vue'
import CloseSelected from '@/Components/Search/CloseSelected.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Activity} from '@/Types/Events/Packs/Activities/Activity'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	pack:Pack,
	event:Event,
	activity:Activity,
}>()

const pack:Ref<Pack> = ref(props.pack)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const activity:Ref<Activity> = ref(props.activity)

function save():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.packs.activities.update', {event: event.value.id, pack: pack.value.id, activity: activity.value.id}), activity.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function selectCompany(company:Company):void{
	activity.value.company_id = company.id
	activity.value.company = company
}

function unselectCompany():void{
	activity.value.company_id = null
	activity.value.company = null
}
</script>
